"use client";

import React, { useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { useDebounce } from "@/hooks/useDebounce";
import { useRouter } from "next/navigation";
import useClickOutside from "@/hooks/useClickOutside";
import useStoreKeys from "@/hooks/useStoreKeys";
import {
	IntelliSuggestItem,
	NewsType,
	ProductData,
	SearchDropdownProduct,
} from "@/lib/5874/types";
import { getSearchSpringClient } from "@/lib/searchspring/client";
import { getArticlesSortedByDate } from "@/lib/builder/server-quries";
import TextInput from "@/components/UI/Input/TextInput/TextInput";
import SearchDropDown from "./SearchDropDown/SearchDropDown";
import SearchIcon from "@/app/assets/images/svg/search.svg";
import ClearIcon from "@/app/assets/images/svg/xmark.svg";
import IconMiniLogo from "@/components/UI/Icon/IconMiniLogo/IconMiniLogo";
import styles from "./SearchBar.module.scss";
import {
	getProductsByProductChildSkus,
	getProductsWithoutChildrenByIds,
} from "@/lib/bigcommerce/product";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import { BigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";
import { isValidJSON } from "@/utils/is-valid-json";

interface SearchBarProps {
	placeHolderText?: string;
	ariaLabel?: string;
	className?: string;
}

interface SearchResultWithStatus<T> {
	isLoading: boolean;
	result: T;
}

export type SearchDropdownDataType = {
	suggestions: SearchResultWithStatus<string[]>;
	products: SearchResultWithStatus<SearchDropdownProduct[]>;
	articles: SearchResultWithStatus<NewsType[]>;
};

const getSearchDropdownInitialValues = (
	isLoading: boolean,
): SearchDropdownDataType => ({
	suggestions: {
		isLoading,
		result: [],
	},
	products: {
		isLoading,
		result: [],
	},
	articles: {
		isLoading,
		result: [],
	},
});

const SearchBar = ({
	placeHolderText = "Search product, sky or brands",
	ariaLabel,
	className = "",
}: SearchBarProps) => {
	const { store, lang } = useStoreKeys();
	const router = useRouter();
	const [datalayerProducts, setDatalayerProducts] = useState<
		Array<ProductData>
	>([]);
	const [searchEntry, setSearchEntry] = useState<string>("");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const { session } = useSessionContext();
	const [searchDropdownData, setSearchDropdownData] =
		useState<SearchDropdownDataType>(getSearchDropdownInitialValues(false));
	const debouncedSearch = useDebounce(searchEntry);
	const inputRef = React.useRef<HTMLInputElement>(null);

	// Datalayer
	const { viewSearchResults } = useDataLayerContext();
	const scope = "search_dropdown";
	const isLoggedIn = session !== null;
	const { currencyFormattingRules } = useContext(BigCommerceStoreContext);

	useEffect(() => {
		if (searchEntry.length >= 3) {
			setIsDropdownOpen(true);
			handleTypeAndSearch(String(debouncedSearch));
		} else {
			setIsDropdownOpen(false);
		}
	}, [debouncedSearch]);

	const handleTypeAndSearch = async (value: string) => {
		setSearchDropdownData(getSearchDropdownInitialValues(true));
		try {
			const client = getSearchSpringClient(store, lang);
			client.getSearchSuggestions(value).then((suggestions) => {
				setSearchDropdownData((searchDropdownData) => ({
					...searchDropdownData,
					suggestions: {
						isLoading: false,
						result: suggestions || [],
					},
				}));
			});
			let autoCompleteResults: IntelliSuggestItem[] = [];
			client
				.getSearchAutoCompleteResults(value)
				.then((results) => {
					autoCompleteResults = results;
					setSearchDropdownData((searchDropdownData) => ({
						...searchDropdownData,
						products: {
							isLoading: false,
							result: results || [],
						},
					}));
				})
				.finally(async () => {
					const productEntityIds = autoCompleteResults
						?.map((result: { uid: string }) => {
							return parseInt(result.uid);
						})
						.filter(Boolean);

					if (productEntityIds?.length) {
						// This code is quite hard to understand.
						// We need to display the price of products. Initially, it seems we can use only getProductsWithoutChildrenByIds because it provides the price.
						// However, the issue is that if the product has children, we need to display a price range, from the lowest to the highest price.
						// For this, we need to fetch the children.

						// But fetching the children takes more time to display the price.

						// The solution is as follows:

						// 1. Use getProductsWithoutChildrenByIds.
						// 2. Split the result of this request into two:
						// 		productsWithChildSkus - products that need a price range.
						// 		productsWithoutChildSkus - products without children.
						// 3. Display the price for productsWithoutChildSkus in the dropdown.
						// 4. Create a list of childSKUs containing all child SKUs from productsWithChildSkus.
						// 5. Call getProductsWithChildrenBySkus.
						// 6. From the response of step 5, add the child products to the parent products (based on product.warranty).
						// 7. Update the dropdown to display the price range for products with children.

						// The truth is, I think this process can be simplified a bit by using the variants property in the GraphQL request from step 1.
						// This way, we would directly receive the list of children and avoid all the later manipulations.
						// (However, at the moment, I'm not sure if childSKUs and variants are always the same product list,
						// which is why I opted for this approach, as we quickly need a solution.)

						let products: ProductData[] = [];
						// Price is displayed only for logged in users
						if (session) {
							products =
								(await getProductsWithoutChildrenByIds(productEntityIds)) || [];
							if (products.length) {
								setDatalayerProducts(products);
							}
						}
						const [productsWithChildSkus, productsWithoutChildSkus] =
							products.reduce(
								([withSkus, withoutSkus], product) => {
									const warrantyParsed = isValidJSON(product.warranty);
									return warrantyParsed?.childSKUs?.length
										? [[...withSkus, product], withoutSkus]
										: [withSkus, [...withoutSkus, product]];
								},
								[[], []] as ProductData[][],
							);
						const updatedProductsWithoutChildSkus = autoCompleteResults.map(
							(result) => {
								const bcProduct = productsWithoutChildSkus.find(
									(product) => product.id === parseInt(result.uid),
								);
								if (!bcProduct) return result;
								return {
									...result,
									isPriceLoaded: true,
									bcProduct,
									customFields: bcProduct.customFields || [],
								};
							},
						);
						setSearchDropdownData((searchDropdownData) => ({
							...searchDropdownData,
							products: {
								isLoading: false,
								result: updatedProductsWithoutChildSkus,
							},
						}));
						const productsWithChildrenBySkus =
							await getProductsByProductChildSkus(productsWithChildSkus);

						const allUpdatedProducts = updatedProductsWithoutChildSkus.map(
							(result) => {
								const bcProduct = productsWithChildrenBySkus.find(
									(product) => product.id === parseInt(result.uid),
								);
								if (!bcProduct) return result;
								return {
									...result,
									isPriceLoaded: true,
									bcProduct,
									customFields: bcProduct.customFields || [],
								};
							},
						);
						setSearchDropdownData((searchDropdownData) => ({
							...searchDropdownData,
							products: {
								isLoading: false,
								result: allUpdatedProducts,
							},
						}));

						viewSearchResults(
							products,
							searchEntry,
							scope,
							currencyFormattingRules.currency,
							isLoggedIn,
							scope,
							scope,
						);
					}
				});

			getArticlesSortedByDate("news", {
				webStore: store,
				searchTerm: searchEntry,
				limit: 5,
			}).then((articles) => {
				setSearchDropdownData((searchDropdownData) => ({
					...searchDropdownData,
					articles: {
						isLoading: false,
						result: (articles as NewsType[]) || [],
					},
				}));
			});
			setIsDropdownOpen(true);
		} catch (err) {
			console.error(err);
		}
	};

	const clearSearchBar = () => {
		setSearchEntry("");
		setSearchDropdownData(getSearchDropdownInitialValues(false));
		inputRef.current?.focus();
		setIsDropdownOpen(false);
	};

	const handleFocus = async () => {
		if (searchEntry.length >= 3) setIsDropdownOpen(true);
		if (searchEntry.length === 0) {
			setSearchDropdownData((prevData) => ({
				...prevData,
				suggestions: {
					result: [],
					isLoading: true,
				},
			}));
			const client = getSearchSpringClient(store, lang);
			const searchSuggestions = await client.getSearchTrendingSuggestions();
			setSearchDropdownData((prevData) => ({
				...prevData,
				suggestions: {
					result: searchSuggestions?.trending?.queries || [],
					isLoading: false,
				},
			}));
		}
	};

	const searchOnEnter = (event: any) => {
		if (event.key === "Enter") {
			handleGotoSearchPage();
			clearSearchBar();
		}
	};

	const handleGotoSearchPage = () => {
		if (searchEntry.length >= 3) {
			inputRef.current?.blur();
			setIsDropdownOpen(false);
			router.push(`/${store}/${lang}/search?query=${searchEntry}`);
		}
	};

	const searchRef = useClickOutside(() => {
		if (isDropdownOpen) {
			setIsDropdownOpen(false);
		}
	});

	return (
		<>
			{isDropdownOpen && <div className={styles.overlay} />}
			<div
				ref={searchRef}
				className={clsx([
					styles.outer,
					isDropdownOpen ? styles.active : "",
					"search-bar",
				])}
			>
				<div className={clsx([styles.searchBox, className])}>
					<TextInput
						ref={inputRef}
						title='Search'
						type='text'
						className={styles.searchBar}
						onKeyDown={searchOnEnter}
						onChange={(e) => {
							setSearchEntry(e.target.value);
						}}
						onFocus={handleFocus}
						value={searchEntry}
						placeholder={placeHolderText}
						aria-label={ariaLabel}
					/>
					{searchEntry && (
						<ClearIcon
							className={styles.clearIcon}
							onClick={clearSearchBar}
							onKeyDown={(e: any) => {
								if (e.key === "Enter") clearSearchBar();
							}}
							tabIndex={0}
							aria-label='Clear Search'
						/>
					)}
					{!searchDropdownData.articles.isLoading &&
					!searchDropdownData.products.isLoading &&
					!searchDropdownData.suggestions.isLoading ? (
						<SearchIcon
							className={styles.searchIcon}
							onClick={handleGotoSearchPage}
							onKeyDown={(e: any) => {
								if (e.key === "Enter") handleGotoSearchPage();
							}}
							tabIndex={0}
							aria-label='Search'
						/>
					) : (
						<IconMiniLogo className={styles.loadingIcon} />
					)}
				</div>
				{isDropdownOpen && (
					<SearchDropDown
						searchEntry={searchEntry}
						datalayerProducts={datalayerProducts}
						searchDropdownData={searchDropdownData}
						handleSuggestionClick={setSearchEntry}
						handleCloseClick={() => setIsDropdownOpen(false)}
					/>
				)}
			</div>
		</>
	);
};

export default SearchBar;
